import { useEffect, useRef, useState } from 'react';
import { dashboardAPI } from '../../../services/dashboard-api';
import { track } from '../../../utils/analytics';
import { clampWindow, presetRange, yesterdayYmd } from '../../../utils/buyerInsightsDates';
import { HISTORY_START, MATCHES_FROM } from './constants';

export const DEFAULT_BOUNDS = { min_start: HISTORY_START, max_end: yesterdayYmd(), matches_from: MATCHES_FROM };
const NO_LIVE = { mode: 'any', from: '', to: '' };

/**
 * Filter state shared by the Products and Brands tabs: activity window (clamped to the server's
 * bounds), Brand, Category, Product status, Product live date, Sale, Sold out at (+ its % off range
 * and the retailer's saved cut points), search (debounced 300 ms) and the /filters meta (flags,
 * options). `initial` seeds the state once (a link from the other tab).
 */
export default function useBuyerInsightsFilters({ tab, myRetailerId = '', initial = {}, onFiltersChange }) {
  const [meta, setMeta] = useState(null);
  const [window, setWindow] = useState(() => initial.window || presetRange(13, DEFAULT_BOUNDS.max_end));
  const [brand, setBrand] = useState(() => initial.brand || []);
  const [category, setCategory] = useState(() => initial.category || []);
  const [status, setStatus] = useState(() => initial.status || 'all');
  const [live, setLive] = useState(() => initial.live || NO_LIVE);
  const [sale, setSale] = useState(() => initial.sale || 'all');
  // "Sold out at" (sold-out-at spec §4): 'all' | 'full_price' | 'discounted'; offRange = {min, max} of
  // one Discounted bucket (null = every discounted sell-out); bands = the retailer's cut points.
  const [soldOutAt, setSoldOutAt] = useState(() => initial.soldOutAt || 'all');
  const [offRange, setOffRange] = useState(() => initial.offRange || null);
  const [bands, setBands] = useState(() => initial.bands || []);
  const bandsSeeded = useRef(Array.isArray(initial.bands)); // the saved default seeds once, never over the user's typing
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  const bounds = meta?.window ? { ...DEFAULT_BOUNDS, ...meta.window } : DEFAULT_BOUNDS;
  const hasFilters = Boolean(
    brand.length ||
      category.length ||
      status !== 'all' ||
      (live.mode && live.mode !== 'any') ||
      sale !== 'all' ||
      soldOutAt !== 'all'
  );
  const filterChanged = (filter, value, extra = {}) =>
    track('Buyer Insights Filter Changed', { tab, filter, value, ...extra });
  const onWindowChange = (next, source) => {
    setWindow(next);
    filterChanged(source === 'preset' ? 'preset' : 'activity_window', `${next.start}_${next.end}`);
  };

  // Apply saves the cut points as the retailer's default (spec §5.4, Daniel 2026-10-16): one action,
  // and Make and Claude use the same buckets from then on. A failed save keeps the bands locally.
  const applyBands = (next) => {
    setBands(next);
    filterChanged('bands', next.join(','));
    dashboardAPI.saveBuyerInsightsSettings({ sold_out_at_bands: next }).catch(() => {});
  };

  // The fields both tabs share travel with the user (spec §4.3): DashboardHome keeps them and
  // seeds the other tab's `initial` when it mounts. Product status and search stay per tab.
  useEffect(() => {
    if (onFiltersChange) onFiltersChange({ window, brand, category, live, sale, soldOutAt, offRange, bands });
  }, [window, brand, category, live, sale, soldOutAt, offRange, bands]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flags, bounds and options — per window + store.
  useEffect(() => {
    let cancelled = false;
    dashboardAPI
      .getBuyerInsightsFilters({ start: window.start, end: window.end, my_retailer_id: myRetailerId || undefined })
      .then((res) => {
        if (cancelled || !res?.success) return;
        setMeta(res.data);
        if (res.data?.window) {
          setWindow((w) => {
            const c = clampWindow(w, res.data.window);
            return c.start === w.start && c.end === w.end ? w : c;
          });
        }
        if (!bandsSeeded.current && Array.isArray(res.data?.sold_out_at_bands)) {
          bandsSeeded.current = true;
          setBands(res.data.sold_out_at_bands);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [window.start, window.end, myRetailerId]);

  // Search debounce.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ((prev) => {
        if (prev !== q) track('Buyer Insights Filter Changed', { tab, filter: 'search', value: q.length });
        return q;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [q, tab]);

  return {
    meta,
    window,
    setWindow,
    bounds,
    brand,
    setBrand,
    category,
    setCategory,
    status,
    setStatus,
    live,
    setLive,
    sale,
    setSale,
    soldOutAt,
    setSoldOutAt,
    offRange,
    setOffRange,
    bands,
    applyBands,
    q,
    setQ,
    debouncedQ,
    hasFilters,
    filterChanged,
    onWindowChange,
  };
}
